<template>
  <div class="map-container">
    <!-- Search Location with Autocomplete Field -->
      <div v-if="!props.readOnly" class="autocomplete-container">
      <label class="autocomplete-label">Search Location *</label>
      <div ref="autocompleteContainer" class="autocomplete-input-wrapper">
        <input
          ref="autocompleteInput"
          v-model="searchQuery"
          class="autocomplete-input"
          placeholder="Enter a location (e.g., Mogadishu, Somalia)"
          @focus="handleInputFocus"
          :disabled="isSearching"
        />
        <div class="autocomplete-icons">
          <v-progress-circular
            v-if="isSearching"
            indeterminate
            size="20"
            width="2"
            color="primary"
          ></v-progress-circular>
          <button
            v-else-if="searchQuery"
            class="clear-button"
            @click="clearSearch"
            type="button"
            aria-label="Clear search"
          >
            <v-icon size="16">mdi-close</v-icon>
          </button>
          <v-icon v-else color="grey" size="20">mdi-magnify</v-icon>
        </div>
      </div>
      <div v-if="searchError" class="error-text">{{ searchError }}</div>
    </div>

    <!-- Location Status Alert -->
    <v-alert
      v-if="selectedLocationInfo"
      type="success"
      dense
      outlined
      class="mb-3 location-alert"
    >
      <div class="d-flex align-center">
        <v-icon left color="success" size="20">mdi-map-marker-check</v-icon>
        <div class="flex-grow-1">
          <div class="text-body-2 font-weight-medium">{{ props.readOnly ? 'Location' : 'Selected Location:' }}</div>
          <div class="text-caption text-truncate">{{ selectedLocationInfo }}</div>
          <div class="text-caption text-truncate location-coordinates" v-if="selectedCoordinates">
            {{ selectedCoordinates }}
          </div>
        </div>
        <v-btn
          v-if="!props.readOnly"
          x-small
          text
          color="error"
          @click="clearLocation"
          class="ml-2"
        >
          Clear
        </v-btn>
      </div>
    </v-alert>

    <!-- Map Loading State -->
    <div v-if="isMapLoading" class="map-loading">
      <v-progress-circular 
        indeterminate 
        color="primary"
        size="40"
      ></v-progress-circular>
      <p class="mt-3 text-body-2 text-center">Loading map...</p>
    </div>

    <!-- Map Container -->
    <div 
      v-show="!isMapLoading"
      ref="mapContainer" 
      class="google-map"
      :style="mapContainerStyle"
    ></div>

    <!-- Instructions Card - Hide in read-only mode -->
    <v-card v-if="!props.readOnly" flat class="mt-3 pa-3 instruction-card" color="grey lighten-5">
      <v-row align="center" no-gutters>
        <v-col cols="auto">
          <v-icon color="info" size="20">mdi-information-outline</v-icon>
        </v-col>
        <v-col class="ml-3">
          <p class="text-body-2 mb-0">
            Type an address for suggestions or click the map to select your exact location.
            You can zoom in for better precision.
          </p>
        </v-col>
      </v-row>
    </v-card>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showErrorSnackbar"
      color="error"
      timeout="5000"
      bottom
    >
      {{ errorMessage }}
      <template #action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showErrorSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="showSuccessSnackbar"
      color="success"
      timeout="3000"
      bottom
    >
      {{ successMessage }}
      <template #action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showSuccessSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import { Loader } from '@googlemaps/js-api-loader';
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
//import { useStore } from "vuex";

// Props
const props = defineProps({
  cities: {
    type: Array,
    default: () => []
  },
  required: {
    type: Boolean,
    default: true
  },
  defaultZoom: {
    type: Number,
    default: 10
  },
  height: {
    type: String,
    default: '500px'
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  cityName: {
    type: String,
    default: null
  },
  placeId: {
    type: String,
    default: null
  },
  selectedLat: {
    type: [Number, String],
    default: null
  },
  selectedLng: {
    type: [Number, String],
    default: null
  }
});

// Emits
const emit = defineEmits(['locationSelected', 'locationCleared', 'error']);

//const store = useStore();

// Environment variables
const googleMapsApiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

// Template refs
const mapContainer = ref(null);
const autocompleteInput = ref(null);
const autocompleteContainer = ref(null);

// Reactive state
const map = ref(null);
const marker = ref(null);
const infoWindow = ref(null);
const geocoder = ref(null);
const placesAutocomplete = ref(null);
const center = ref({ lat: 2.0392, lng: 45.3419 }); // Default to Mogadishu
const searchQuery = ref("");
const isSearching = ref(false);
const isMapLoading = ref(true);
const searchError = ref("");
const errorMessage = ref("");
const successMessage = ref("");
const showErrorSnackbar = ref(false);
const showSuccessSnackbar = ref(false);

// Selected location state
const selectedLocation = ref(null);
//const selectedCity = computed(() => store.getters["NewCategory/selectedCity"]);

// Update selectedLocationInfo to only show the map's selection
const selectedLocationInfo = computed(() => {
  if (selectedLocation.value) {
    return selectedLocation.value.formattedAddress || selectedLocation.value.name || "Selected Location";
  }
  return null;
});


const selectedCoordinates = computed(() => {
  if (selectedLocation.value?.lat && selectedLocation.value?.lng) {
    return `${selectedLocation.value.lat.toFixed(6)}, ${selectedLocation.value.lng.toFixed(6)}`;
  }
  return null;
});

const mapContainerStyle = computed(() => ({
  width: "100%",
  height: props.height,
  borderRadius: "8px",
  border: "1px solid #ddd"
}));

// Google Maps Loader
const loader = new Loader({
  apiKey: googleMapsApiKey,
  version: "weekly",
  libraries: ["places", "geometry"]
});

// Initialize Google Maps with special handling for read-only mode
const initializeMap = async () => {
  try {
    isMapLoading.value = true;
    
    await loader.load();
    
    if (!mapContainer.value) {
      throw new Error("Map container not found");
    }

    // Initialize map with different options based on mode
    map.value = new google.maps.Map(mapContainer.value, {
      center: center.value,
      zoom: props.readOnly ? 15 : props.defaultZoom, // Higher zoom in read-only
      mapTypeControl: !props.readOnly, // Hide map type control in read-only
      streetViewControl: true,
      fullscreenControl: !props.readOnly, // Hide fullscreen in read-only
      zoomControl: true,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "on" }]
        }
      ]
    });

    // Initialize geocoder
    geocoder.value = new google.maps.Geocoder();

    // Initialize info window
    infoWindow.value = new google.maps.InfoWindow({
      maxWidth: 300
    });

    // Only add click listener when not in read-only mode
    if (!props.readOnly) {
      map.value.addListener("click", handleMapClick);
    }

    // Initialize autocomplete after map is loaded (only if not read-only)
    if (!props.readOnly && autocompleteInput.value) {
      initPlacesAutocomplete();
    }

    // For read-only mode, use the provided coordinates directly
    if (props.readOnly && props.selectedLat && props.selectedLng) {
      updateMapLocation({
        lat: parseFloat(props.selectedLat),
        lng: parseFloat(props.selectedLng),
        name: props.cityName || "Selected Location",
        placeId: props.placeId || "manual-location",
        formattedAddress: props.cityName || "Selected Location"
      });
    }
    // Otherwise load existing location from store if available
    // else {
    //   const existingLocation = selectedCity.value;
    //   if (existingLocation?.lat && existingLocation?.lng) {
    //     updateMapLocation({
    //       lat: parseFloat(existingLocation.lat),
    //       lng: parseFloat(existingLocation.lng),
    //       name: existingLocation.name,
    //       placeId: existingLocation.id,
    //       formattedAddress: existingLocation.formatted_address || existingLocation.name
    //     });
    //   }
    // }

    isMapLoading.value = false;
    
    // Only show success message in interactive mode
    if (!props.readOnly) {
      showSuccess("Map loaded successfully!");
    }

  } catch (error) {
    console.error("Error initializing map:", error);
    isMapLoading.value = false;
    showError("Failed to load map. Please refresh the page.");
  }
};

// Initialize Google Places Autocomplete
const initPlacesAutocomplete = () => {
  if (!window.google || !window.google.maps || !window.google.maps.places || !autocompleteInput.value) {
    console.error("Google Maps Places API not loaded or input element not found");
    return;
  }

  try {
    // Create autocomplete instance
    placesAutocomplete.value = new google.maps.places.Autocomplete(autocompleteInput.value, {
      fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components'],
      types: ['geocode', 'establishment'] // Search for all types of locations
    });
    
    // Add event listener for place selection
    placesAutocomplete.value.addListener('place_changed', () => {
      const place = placesAutocomplete.value.getPlace();
      handlePlaceSelection(place);
    });
  } catch (error) {
    console.error("Error initializing Places Autocomplete:", error);
    showError("Failed to initialize location search. Please try using the map directly.");
  }
};

// Handle selected place from autocomplete
const handlePlaceSelection = (place) => {
  // Reset search state
  isSearching.value = false;
  searchError.value = "";

  // Check if a valid place with geometry was selected
  if (!place || !place.geometry || !place.geometry.location) {
    searchError.value = "Invalid location selected. Please try again.";
    return;
  }

  // Extract city name from address components if available
  let cityName = place.name || '';
  let cityComponent = null;
  
  if (place.address_components) {
    cityComponent = place.address_components.find(component => 
      component.types.includes('locality') || 
      component.types.includes('administrative_area_level_1')
    );
    
    if (cityComponent) {
      cityName = cityComponent.long_name;
    }
  }

  // Create location data object with both place details and exact coordinates
  const locationData = {
    placeId: place.place_id,
    id: place.place_id, // For Vuex store compatibility
    name: cityName,
    lat: place.geometry.location.lat(),
    lng: place.geometry.location.lng(),
    formattedAddress: place.formatted_address,
    cityName: cityName,
    // Include raw place data for advanced usage
    rawPlaceData: {
      placeId: place.place_id,
      name: place.name,
      formattedAddress: place.formatted_address,
      addressComponents: place.address_components || []
    }
  };

  // Update map and store
  updateMapLocation(locationData);
  
  // Update Vuex store with location data
  updateStoreAndEmitLocation(locationData);
  
  // Clear the search input
  searchQuery.value = "";
};


// Update the map location with marker - modify for read-only mode
const updateMapLocation = (locationData) => {
  if (!map.value) return;

  const position = {
    lat: parseFloat(locationData.lat),
    lng: parseFloat(locationData.lng)
  };

  // Save selected location
  selectedLocation.value = locationData;

  // Remove existing marker
  if (marker.value) {
    marker.value.setMap(null);
  }

  // Create new marker with animated drop
  marker.value = new google.maps.Marker({
    position: position,
    map: map.value,
    title: locationData.name || locationData.formattedAddress || "Selected Location",
    animation: google.maps.Animation.DROP,
    icon: {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.163 0 0 7.163 0 16c0 16 16 26 16 26s16-10 16-26C32 7.163 24.837 0 16 0z" fill="#b30000"/>
          <circle cx="16" cy="16" r="6" fill="white"/>
        </svg>
      `),
      scaledSize: new google.maps.Size(32, 42),
      anchor: new google.maps.Point(16, 42)
    }
  });

  // Only add click listener to marker in interactive mode
  if (!props.readOnly) {
    marker.value.addListener("click", () => {
      showInfoWindow(position, locationData);
    });
  }

  // Center map on location and zoom in
  map.value.setCenter(position);
  map.value.setZoom(props.readOnly ? 15 : 16); // Slightly different zoom in read-only mode
};

// Show info window with location details and actions
const showInfoWindow = (position, locationData) => {
  if (!infoWindow.value) return;

  const content = `
    <div class="info-window-content">
      <h4 class="info-window-title">Selected Location</h4>
      <p class="info-window-address">${locationData.formattedAddress || locationData.name}</p>
      <p class="info-window-coordinates">Lat: ${locationData.lat.toFixed(6)}, Lng: ${locationData.lng.toFixed(6)}</p>
      <div class="info-window-actions">
        <button id="clear-location-btn" class="clear-location-btn">
          Clear Location
        </button>
      </div>
    </div>
  `;

  infoWindow.value.setContent(content);
  infoWindow.value.setPosition(position);
  infoWindow.value.open(map.value);

  // Add event listener to the clear button after the info window is opened
  google.maps.event.addListenerOnce(infoWindow.value, 'domready', () => {
    const clearButton = document.getElementById('clear-location-btn');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        clearLocation();
        infoWindow.value.close();
      });
    }
  });
};

// Handle map click for direct location selection
const handleMapClick = async (event) => {
  const lat = event.latLng.lat();
  const lng = event.latLng.lng();

  isSearching.value = true;
  searchError.value = "";

  try {
    // Use Google's geocoding service for reverse geocoding
    const response = await new Promise((resolve, reject) => {
      if (!geocoder.value) {
        reject(new Error("Geocoder not initialized"));
        return;
      }

      geocoder.value.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            resolve(results[0]);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        }
      );
    });

    // Extract city name from address components
    let cityName = "";
    let cityComponent = null;
    
    if (response.address_components) {
      cityComponent = response.address_components.find(component => 
        component.types.includes('locality') || 
        component.types.includes('administrative_area_level_1')
      );
      
      if (cityComponent) {
        cityName = cityComponent.long_name;
      }
    }

    // Create location data object
    const locationData = {
      placeId: response.place_id,
      id: response.place_id, // For Vuex store compatibility
      name: cityName || "Selected Location",
      lat: lat,
      lng: lng,
      formattedAddress: response.formatted_address,
      cityName: cityName || response.formatted_address.split(',')[0],
      // Include raw place data
      rawPlaceData: {
        placeId: response.place_id,
        name: cityName || response.formatted_address.split(',')[0],
        formattedAddress: response.formatted_address,
        addressComponents: response.address_components || []
      }
    };

    // Update map and store
    updateMapLocation(locationData);
    updateStoreAndEmitLocation(locationData);
    
    showSuccess("Location selected successfully!");

  } catch (error) {
    console.error("Error with reverse geocoding:", error);
    
    // Still allow manual location selection without geocoding details
    const locationData = {
      placeId: `manual-${lat}-${lng}`,
      id: `manual-${lat}-${lng}`, // For Vuex store compatibility
      name: "Manual Location",
      lat: lat,
      lng: lng,
      formattedAddress: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`,
      cityName: "Unknown Location"
    };
    
    updateMapLocation(locationData);
    updateStoreAndEmitLocation(locationData);
    
    showSuccess("Exact coordinates selected!");
  } finally {
    isSearching.value = false;
  }
};

// Update Vuex store and emit location data
// const updateStoreAndEmitLocation = async (locationData) => {
//   try {
//     // Prepare data for Vuex store
//     const storeData = {
//       id: locationData.placeId,
//       name: locationData.cityName || locationData.name,
//       city_name: locationData.cityName || locationData.name,
//       lat: locationData.lat,
//       lng: locationData.lng,
//       formatted_address: locationData.formattedAddress,
//       google_place_id: locationData.placeId
//     };

//     // Update Vuex store
//     await store.dispatch("NewCategory/selectCity", storeData);
    
//     // Emit complete location data to parent
//     emit('locationSelected', {
//       id: locationData.placeId, // This will be cityId in the backend
//       name: locationData.cityName || locationData.name,
//       cityName: locationData.cityName || locationData.name,
//       lat: locationData.lat,
//       lng: locationData.lng,
//       selectedLat: locationData.lat,
//       selectedLng: locationData.lng,
//       google_place_id: locationData.placeId,
//       formattedAddress: locationData.formattedAddress,
//       // Include location object for Location model
//       location: {
//         name: locationData.formattedAddress,
//         lat: locationData.lat,
//         lng: locationData.lng
//       }
//     });
//   } catch (error) {
//     console.error("Error updating location data:", error);
//     showError("Error saving location data");
//   }
// };
// In updateStoreAndEmitLocation, REMOVE Vuex store dispatch, only emit:
const updateStoreAndEmitLocation = async (locationData) => {
  try {
    emit('locationSelected', {
      id: locationData.placeId,
      name: locationData.cityName || locationData.name,
      cityName: locationData.cityName || locationData.name,
      lat: locationData.lat,
      lng: locationData.lng,
      selectedLat: locationData.lat,
      selectedLng: locationData.lng,
      google_place_id: locationData.placeId,
      formattedAddress: locationData.formattedAddress,
      location: {
        name: locationData.formattedAddress,
        lat: locationData.lat,
        lng: locationData.lng
      }
    });
  } catch (error) {
    console.error("Error updating location data:", error);
    showError("Error saving location data");
  }
};


// Handle input focus (for mobile devices)
const handleInputFocus = () => {
  // For mobile devices, this helps ensure the autocomplete suggestions are visible
  if (autocompleteInput.value) {
    autocompleteInput.value.setAttribute('autocomplete', 'none');
  }
};

// Clear search input
const clearSearch = () => {
  searchQuery.value = "";
  searchError.value = "";
};

// Clear location
// const clearLocation = () => {
//   // Remove marker from map
//   if (marker.value) {
//     marker.value.setMap(null);
//     marker.value = null;
//   }

//   // Close info window
//   if (infoWindow.value) {
//     infoWindow.value.close();
//   }

//   // Reset map to default position
//   if (map.value) {
//     map.value.setCenter(center.value);
//     map.value.setZoom(props.defaultZoom);
//   }

//   // Clear selected location
//   selectedLocation.value = null;

//   // Clear store
//   store.dispatch("NewCategory/selectCity", null);
  
//   // Emit to parent
//   emit('locationCleared');
  
//   // Clear any errors
//   searchError.value = "";
  
//   showSuccess("Location cleared successfully!");
// };
// In clearLocation, REMOVE store.dispatch("NewCategory/selectCity", null);
const clearLocation = () => {
  if (marker.value) {
    marker.value.setMap(null);
    marker.value = null;
  }
  if (infoWindow.value) {
    infoWindow.value.close();
  }
  if (map.value) {
    map.value.setCenter(center.value);
    map.value.setZoom(props.defaultZoom);
  }
  selectedLocation.value = null;
  emit('locationCleared');
  searchError.value = "";
  showSuccess("Location cleared successfully!");
};

// Utility functions for notifications
const showError = (message) => {
  errorMessage.value = message;
  showErrorSnackbar.value = true;
  emit('error', message);
};

const showSuccess = (message) => {
  successMessage.value = message;
  showSuccessSnackbar.value = true;
};

// Validation method for parent form
const validateLocation = () => {
  if (props.required && !selectedLocation.value) {
    return "Please select a location on the map";  // Error message
  }
  return true; // Location is valid
};

// Lifecycle hooks
onMounted(async () => {
  await nextTick();
  
  if (!googleMapsApiKey) {
    showError("Google Maps API key is not configured");
    return;
  }
  
  await initializeMap();
});

onUnmounted(() => {
  // Cleanup
  if (marker.value) {
    marker.value.setMap(null);
  }
  
  if (infoWindow.value) {
    infoWindow.value.close();
  }
  
  // Clean up Places Autocomplete
  if (placesAutocomplete.value) {
    google.maps.event.clearInstanceListeners(placesAutocomplete.value);
  }
});

// Expose methods for parent component
defineExpose({
  validateLocation,
  clearLocation,
  selectedLocation: computed(() => selectedLocation.value ),
  isMapReady: computed(() => !!map.value)
});
</script>

<style scoped>
/* Read-only mode styling */
.map-container.read-only {
  margin-top: 0;
}

.google-map.read-only {
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  border: 1px solid rgba(0,0,0,0.1);
}

.location-alert.read-only {
  margin-bottom: 8px !important;
}
.map-container {
  width: 100%;
  margin-top: 16px;
}

/* Enhanced Autocomplete Input */
.autocomplete-container {
  margin-bottom: 16px;
  width: 100%;
}

.autocomplete-label {
  display: block;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(0,0,0,0.85);
}

.autocomplete-input-wrapper {
  position: relative;
  width: 100%;
  border-radius: 4px;
  border: 1px solid rgba(0,0,0,0.23);
  display: flex;
  align-items: center;
  background: white;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.autocomplete-input-wrapper:focus-within {
  border-color: #b30000;
  box-shadow: 0 0 0 1px #b30000;
}

.autocomplete-input {
  flex-grow: 1;
  border: none;
  outline: none;
  padding: 12px 16px;
  font-size: 16px;
  background: transparent;
  width: 100%;
  color: rgba(0,0,0,0.87);
}

.autocomplete-icons {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 12px;
}

.clear-button {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0,0,0,0.54);
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.clear-button:hover {
  background-color: rgba(0,0,0,0.04);
}

.error-text {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  padding-left: 4px;
}

.google-map {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.map-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f8f9fa;
}

.location-alert {
  border-radius: 8px !important;
}

.instruction-card {
  border-radius: 8px !important;
  border: 1px solid rgba(0,0,0,0.1);
}

.location-coordinates {
  color: #1976d2;
  font-weight: 500;
  margin-top: 2px;
}

/* Info Window Custom Styling (applies through global styles) */
:global(.info-window-content) {
  padding: 8px 0;
}

:global(.info-window-title) {
  margin: 0 0 8px 0;
  color: #b30000;
  font-size: 16px;
  font-weight: 600;
}

:global(.info-window-address) {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
  line-height: 1.4;
}

:global(.info-window-coordinates) {
  margin: 0 0 12px 0;
  font-size: 12px;
  color: #1976d2;
  font-weight: 500;
}

:global(.info-window-actions) {
  text-align: center;
}

:global(.clear-location-btn) {
  background: #b30000;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
}

:global(.clear-location-btn:hover) {
  background: #990000;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .map-container {
    margin-top: 12px;
  }
}

@media (max-width: 600px) {
  .google-map {
    height: 400px !important;
  }
  
  .map-loading {
    min-height: 300px;
  }

  .autocomplete-input {
    padding: 10px 12px;
    font-size: 14px;
  }
  
  .autocomplete-label {
    font-size: 14px;
    margin-bottom: 6px;
  }
  
  .location-alert {
    margin-bottom: 12px !important;
  }
  
  .instruction-card {
    margin-top: 12px !important;
    padding: 12px !important;
  }
}

@media (max-width: 400px) {
  .google-map {
    height: 350px !important;
  }
  
  .instruction-card {
    padding: 10px !important;
  }
}

/* Ensure proper touch targets on mobile */
@media (hover: none) and (pointer: coarse) {
  .autocomplete-input {
    font-size: 16px; /* Prevents iOS zoom on focus */
  }
  
  .clear-button {
    min-width: 44px;
    min-height: 44px;
  }
}
</style>